"use client";

import React, { useMemo } from "react";
import { InvestmentOptions } from "@/app/interface/platform";
import Home from "@/app/assets/icons/home";
import Diamond from "@/app/assets/icons/diamond";
import FD from "@/app/assets/icons/fd";
import Bullion from "@/app/assets/icons/bullion";
import UsStocks from "@/app/assets/icons/us-stocks";
// import Refer from "@/app/assets/icons/refer";
import Learn from "@/app/assets/icons/learn";
import Fingerprint from "@/app/assets/icons/finerprint";
import PortfolioIcon from "@/app/assets/icons/portfolio-icon";
import Media from "@/app/assets/icons/media";
import { DirectPaths } from "@/config/routes";
import type { NavItem } from "@/types/nav-item.types";
import Bulb from "../assets/icons/bulb";
import BlogNew from "../assets/icons/blog-new";

interface Props {
    active: string;
    isLoggedIn: boolean;
}

const emptyObject: NavItem = {
    label: "",
    href: "",
    eventName: "",
    branchEvent: "",
    isActive: false,
    isExternal: false,
    logoUrl: ``,
    icon: null,
};

const useNavItems = ({ active, isLoggedIn }: Props): NavItem[] => {
    return useMemo(
        () => [
            {
                label: InvestmentOptions.Home,
                href: isLoggedIn
                    ? String(`${process.env.ICM_POST_URL}`)
                    : String(process.env.ICM_URL),
                isActive: Boolean(active === InvestmentOptions.Home),
                isExternal: false,
                icon: <Home />,
            },
            {
                label: InvestmentOptions.Broking,
                href: String(`${process.env.ICM_URL}${DirectPaths.BROKING}`),
                eventName: "Home_Nav_Bar_Broking",
                branchEvent: "Navigation_Broking",
                isActive: Boolean(active === InvestmentOptions.Broking),
                isExternal: true,
                logoUrl: `${process.env.ICM_BROKING_LOGO_URL}`,
                icon: <Diamond />,
            },
            {
                ...(process.env.SHOW_GLOBAL_STOCKS === "true"
                    ? {
                          label: InvestmentOptions.Global,
                          href: isLoggedIn
                              ? String(
                                    `${process.env.ICM_GLOBAL_POST_URL + DirectPaths.GLOBAL + DirectPaths.US_STOCKS}`,
                                ) || ""
                              : String(`${process.env.ICM_GLOBAL_URL + DirectPaths.US_STOCKS}`),
                          isActive: Boolean(
                              [
                                  InvestmentOptions.USEtf,
                                  InvestmentOptions.USStocks,
                                  InvestmentOptions.Global,
                              ].includes(active as InvestmentOptions),
                          ),
                          branchEvent: "Navigation_US_Stocks",
                          isExternal: false,
                          isNew: true,
                          icon: <UsStocks />,
                          logoUrl: `${process.env.ICM_GLOBAL_LOGO_URL}`,
                          subItems: [
                              {
                                  label: InvestmentOptions.USStocks,
                                  href: isLoggedIn
                                      ? String(
                                            `${process.env.ICM_GLOBAL_POST_URL + DirectPaths.GLOBAL + DirectPaths.US_STOCKS}`,
                                        ) || ""
                                      : String(
                                            `${process.env.ICM_GLOBAL_URL + DirectPaths.US_STOCKS}`,
                                        ),
                                  eventName: "Home_Nav_Bar_US_Stocks",
                                  branchEvent: "Navigation_Shares_US_Stocks",
                                  isActive: Boolean(
                                      [
                                          InvestmentOptions.USStocks,
                                          InvestmentOptions.Global,
                                      ].includes(active as InvestmentOptions),
                                  ),
                                  isExternal: false,
                                  icon: <UsStocks className="h-full w-full" />,
                                  logoUrl: `${process.env.ICM_GLOBAL_LOGO_URL}`,
                              },
                              {
                                  label: InvestmentOptions.USEtf,
                                  href: isLoggedIn
                                      ? String(
                                            `${process.env.ICM_GLOBAL_POST_URL + DirectPaths.GLOBAL + DirectPaths.US_ETFS}`,
                                        ) || ""
                                      : String(
                                            `${process.env.ICM_GLOBAL_URL + DirectPaths.US_ETFS}`,
                                        ),
                                  eventName: "Home_Nav_Bar_US_ETF",
                                  branchEvent: "Navigation_Shares_US_ETF",
                                  isActive: Boolean(
                                      [InvestmentOptions.USEtf, InvestmentOptions.Global].includes(
                                          active as InvestmentOptions,
                                      ),
                                  ),
                                  isExternal: false,
                                  icon: <UsStocks className="h-full w-full" />,
                                  logoUrl: `${process.env.ICM_GLOBAL_LOGO_URL}`,
                              },
                          ],
                      }
                    : { ...emptyObject }),
            },
            {
                label: InvestmentOptions.Alts,
                href: "",
                eventName: "Home_Nav_Bar_Alts",
                branchEvent: "Navigation_Alts",
                isActive: Boolean(
                    [
                        InvestmentOptions.UnlistedShares,
                        InvestmentOptions.FixedDeposits,
                        InvestmentOptions.Bullion,
                        InvestmentOptions.Gold,
                        InvestmentOptions.Silver,
                    ].includes(active as InvestmentOptions),
                ),
                isExternal: false,
                icon: <Diamond />,
                subItems: [
                    /* {
                        label: InvestmentOptions.UnlistedShares,
                        href: isLoggedIn
                            ? String(`${process.env.ICM_UE_POST_URL_LISTING}`) || ""
                            : String(`${process.env.ICM_UE_URL}`),
                        eventName: "Home_Nav_Bar_US",
                        branchEvent: "Navigation_Shares",
                        isActive: Boolean(active === InvestmentOptions.UnlistedShares),
                        isExternal: false,
                        logoUrl: `${process.env.ICM_UE_LOGO_URL}`,
                        icon: <Diamond className="h-full w-full" />,
                    }, */
                    {
                        label: InvestmentOptions.Gold,
                        href:
                            (isLoggedIn
                                ? `${process.env.ICM_BULLION_POST_URL}`
                                : `${process.env.ICM_BULLION_URL}`) +
                                `${DirectPaths.BULLION_POSTFIX_PATH + DirectPaths.GOLD}` || "",
                        isActive: Boolean(active === InvestmentOptions.Gold),
                        branchEvent: "Navigation_Bullion",
                        isExternal: false,
                        isNew: true,
                        logoUrl: `${process.env.ICM_BULLION_LOGO_URL}`,
                        icon: <Bullion className="h-full w-full" />,
                    },
                    {
                        label: InvestmentOptions.FixedDeposits,
                        href:
                            (isLoggedIn
                                ? `${process.env.ICM_FD_POST_URL}`
                                : `${process.env.ICM_FD_URL}`) + DirectPaths.FD_POSTFIX_PATH || "",
                        isActive: Boolean(active === InvestmentOptions.FixedDeposits),
                        branchEvent: "Navigation_FD",
                        isExternal: false,
                        logoUrl: `${process.env.ICM_FD_LOGO_URL}`,
                        icon: <FD className="h-full w-full" />,
                    },
                ],
            },
            {
                label: InvestmentOptions.Portfolio,
                href: `${process.env.ICM_URL}/portfolio`,
                branchEvent: "Navigation_Portfolio",
                isActive: Boolean(active === InvestmentOptions.Portfolio),
                isExternal: false,
                icon: <PortfolioIcon />,
            },
            // {
            //     label: InvestmentOptions.Refer,
            //     href: `${process.env.ICM_URL}/refer`,
            //     eventName: "Refer_Homepage_Top_Nav_bar",
            //     branchEvent: "Navigation_Refer",
            //     isActive: Boolean(active === InvestmentOptions.Refer),
            //     isExternal: false,
            //     icon: <Refer />,
            // },
            {
                label: "About",
                href: `${process.env.ICM_URL}/about`,
                eventName: "Home_Nav_Bar_About_Us",
                isActive: Boolean(active === "About"),
                isExternal: false,
                icon: <Fingerprint />,
            },
            {
                label: "Learn",
                href: "",
                branchEvent: "Navigation_Learn",
                isActive: false,
                isExternal: false,
                icon: <Learn />,
                subItems: [
                    {
                        label: "Blogs",
                        href: `${process.env.ICM_LEARN}`,
                        eventName: "Home_Nav_Bar_Blogs",
                        branchEvent: "Navigation_Blogs",
                        isActive: Boolean(["Blogs", "Learn"].includes(active as string)),
                        isExternal: false,
                        target: "_blank",
                        rel: "noopener noreferrer",
                        icon: <BlogNew />,
                    },
                    {
                        label: "Knowledge Centre",
                        href: `${process.env.ICM_KNOWLEDGE_CENTRE}`,
                        eventName: "Home_Nav_Bar_Knowledge_Center",
                        branchEvent: "Navigation_Knowledge_Center",
                        isActive: Boolean(["Knowledge Centre", "Learn"].includes(active as string)),
                        isExternal: false,
                        target: "_blank",
                        rel: "noopener noreferrer",
                        icon: <Bulb size={24} />,
                    },
                    {
                        label: "Media",
                        href: `${process.env.ICM_MEDIA}`,
                        eventName: "Home_Nav_Bar_Media",
                        branchEvent: "Navigation_Media",
                        isActive: Boolean(["Media", "Learn"].includes(active as string)),
                        isExternal: false,
                        icon: <Media width={24} height={20} />,
                        target: "_blank",
                    },
                ],
            },
        ],
        [isLoggedIn, active],
    );
};

export default useNavItems;
